import {
  Box,
  DialogActions,
  DialogContent,
  DialogTitle,
  Button,
  TextField,
  FormControl,
  InputLabel,
  Select,
  OutlinedInput,
  MenuItem,
  Chip,
  Checkbox,
} from '@mui/material';
import { useFormik } from 'formik';
import { useTranslation } from 'react-i18next';
import { useRecoilState, useRecoilValue, useSetRecoilState } from 'recoil';
import {
  toastStateSelector,
  ToastSeverity,
  dialogState,
  visitTemplateListState,
  selectedVisitTemplateState,
  projectState,
} from 'state';
import { GeneralIds, ModelTypes, VisitTemplateOrderConfig } from 'types/types';
import { useParams } from 'react-router-dom';
import { mutation, query } from 'operations';
import { VisitTemplate } from 'api/models/VisitTemplate';
import { useMemo } from 'react';
import { FormTemplateType } from 'api/models';
import * as yup from 'yup';
import ClearableSelect from 'components/ClearableSelect/ClearableSelect';
import useQuery from 'hooks/useQuery';
import { FormTemplateStatus } from 'api/models/FormTemplate';
import { FormTemplateEditorMode } from 'routes/FormTemplateEditor/FormTemplateEditor';
import useHasAccess from 'hooks/useHasAccess';
import { Permission } from 'state/permissionState';

export function AddVisitTemplateDialog() {
  const closeDialog = useSetRecoilState(dialogState);
  const setToast = useSetRecoilState(toastStateSelector);
  const { t, i18n } = useTranslation();
  const { projectId: id } = useParams<GeneralIds>();
  const [visitTemplates, setVisitTemplateList] = useRecoilState(
    visitTemplateListState,
  );
  const { selectedProject } = useRecoilValue(projectState);
  const [selectedVisitTemplate, setSelectedVisitTemplate] = useRecoilState(
    selectedVisitTemplateState,
  );

  const isProjectAdmin = useHasAccess([Permission.WRITE_PROJECT]);

  const visitTemplateNames = useMemo(() => {
    return visitTemplates.map(visitTemplate => visitTemplate.name);
  }, [visitTemplates]);

  const { data: formTemplateTypes } = useQuery<FormTemplateType[]>({
    type: ModelTypes.formTemplateType,
    queryParams: {
      type: FormTemplateEditorMode.default,
    },
    ids: {
      projectId: id,
    },
  });

  const { data: sampleTemplateTypes } = useQuery<FormTemplateType[]>({
    type: ModelTypes.formTemplateType,
    queryParams: {
      type: FormTemplateEditorMode.sample,
    },
    ids: {
      projectId: id,
    },
  });

  const { data: reportTemplateTypes } = useQuery<FormTemplateType[]>({
    type: ModelTypes.formTemplateType,
    queryParams: {
      type: FormTemplateEditorMode.report,
    },
    ids: {
      projectId: id,
    },
  });

  const formik = useFormik({
    enableReinitialize: true,
    initialValues: {
      orderConfig:
        selectedVisitTemplate?.orderConfig ?? VisitTemplateOrderConfig.any,
      name: selectedVisitTemplate?.name ?? '',
      repeatConfig: selectedVisitTemplate?.repeatConfig ?? 0,
      supportedFormTemplateTypes:
        selectedVisitTemplate?.supportedFormTemplateTypes ?? [],
      supportedSampleFormTemplateTypes:
        selectedVisitTemplate?.supportedSampleFormTemplateTypes ?? [],
      supportedReportTemplateTypes:
        selectedVisitTemplate?.supportedReportTemplateTypes ?? [],
    },
    validationSchema: yup.object({
      // name must be unique among visitTemplates names
      name: yup
        .string()
        .required(t('general.errors.required') ?? '')
        .test(
          'is-unique',
          t('general.errors.hasToBeUniqueVisitName') ?? '',
          function (value) {
            if (
              !selectedVisitTemplate?.id &&
              visitTemplateNames.includes(value)
            ) {
              return false;
            }
            return true;
          },
        ),
    }),
    onSubmit: async (values, { resetForm }) => {
      const {
        orderConfig,
        name,
        repeatConfig,
        supportedFormTemplateTypes,
        supportedSampleFormTemplateTypes,
        supportedReportTemplateTypes,
      } = values;

      setSelectedVisitTemplate(null);

      try {
        await mutation<VisitTemplate>({
          type: ModelTypes.visitTemplate,
          body: {
            id: selectedVisitTemplate?.id ?? '',
            orderConfig,
            name,
            repeatConfig,
            supportedFormTemplateTypes,
            supportedSampleFormTemplateTypes,
            supportedReportTemplateTypes,
          },
          ids: {
            id: selectedVisitTemplate?.id ?? '',
            projectId: id,
          },
        });

        const { data: visitTemplateList } = await query<VisitTemplate[]>({
          type: ModelTypes.visitTemplate,
          ids: {
            projectId: id,
          },
        });
        setVisitTemplateList(visitTemplateList);

        resetForm();
        closeDialog({ isOpen: false });

        setToast({
          isOpen: true,
          message: t('general.success.save'),
          severity: ToastSeverity.success,
        });
      } catch (e: any) {
        const errorCode = e.response?.data?.error?.[0].code;
        setToast({
          isOpen: true,
          message: i18n.exists(`errors.${errorCode}`)
            ? t(`errors.${errorCode}`)
            : t('general.errors.save'),
          severity: ToastSeverity.error,
        });
      }
    },
  });

  return (
    <Box>
      <DialogTitle>
        {selectedVisitTemplate?.id
          ? t('dialogs.editVisitTemplate.title')
          : t('dialogs.addVisitTemplate.title')}
      </DialogTitle>
      <DialogContent>
        <form>
          <TextField
            sx={{ mt: 2 }}
            fullWidth
            label={t('general.name') + ' *'}
            type="name"
            name="name"
            value={formik.values.name}
            onChange={formik.handleChange}
            error={formik.touched.name && Boolean(formik.errors.name)}
            helperText={formik.touched.name && formik.errors.name}
            InputLabelProps={{
              shrink: true,
            }}
          />

          <TextField
            sx={{ mt: 2 }}
            fullWidth
            label={t('visits.repeatConfig')}
            type="repeatConfig"
            name="repeatConfig"
            value={formik.values.repeatConfig}
            onChange={formik.handleChange}
            error={
              formik.touched.repeatConfig && Boolean(formik.errors.repeatConfig)
            }
            helperText={
              formik.touched.repeatConfig && formik.errors.repeatConfig
            }
            InputLabelProps={{
              shrink: true,
            }}
          />

          <FormControl fullWidth sx={{ mt: 2 }}>
            <InputLabel>{t('visits.orderConfig')}</InputLabel>
            <Select
              name="orderConfig"
              value={formik.values.orderConfig}
              onChange={e => {
                formik.handleChange(e);
              }}
              input={<OutlinedInput label={t('visits.orderConfig')} />}
              error={
                formik.touched.orderConfig && Boolean(formik.errors.orderConfig)
              }
            >
              <MenuItem key="first" value="first">
                {t('visits.orderConfigValues.first')}
              </MenuItem>
              <MenuItem key="not_first" value="not_first">
                {t('visits.orderConfigValues.not_first')}
              </MenuItem>
              <MenuItem key="last" value="last">
                {t('visits.orderConfigValues.last')}
              </MenuItem>
              <MenuItem key="any" value="any">
                {t('visits.orderConfigValues.any')}
              </MenuItem>
            </Select>
          </FormControl>

          <ClearableSelect
            sx={{ mt: 2 }}
            fullWidth
            multiple={true}
            isClearable={true}
            onClear={() => {
              formik.setFieldValue('supportedFormTemplateTypes', []);
            }}
            label={t('visits.supportedFormTemplateTypes')}
            name="supportedFormTemplateTypes"
            value={formik.values.supportedFormTemplateTypes}
            onChange={e =>
              formik.setFieldValue('supportedFormTemplateTypes', e.target.value)
            }
            renderValue={() => {
              return formik.values.supportedFormTemplateTypes?.map(
                formTemplateTypeId => (
                  <Chip
                    key={`add-visit-template-dialog:${formTemplateTypeId}-value-chip`}
                    sx={{ mr: 1 }}
                    label={
                      formTemplateTypes?.find(
                        el => el.id === formTemplateTypeId,
                      )?.name
                    }
                    color={'primary'}
                  />
                ),
              );
            }}
            error={
              formik.touched.supportedFormTemplateTypes &&
              Boolean(formik.errors.supportedFormTemplateTypes)
            }
            helperText={
              formik.touched.supportedFormTemplateTypes &&
              formik.errors.supportedFormTemplateTypes
            }
          >
            {formTemplateTypes?.map(type => (
              <MenuItem
                key={`${type.id}-form-template-type-menu-item`}
                value={type.id}
                disabled={
                  type.formTemplates?.length === 1 &&
                  type.formTemplates[0].status === FormTemplateStatus.Draft
                }
              >
                <Checkbox
                  checked={formik.values.supportedFormTemplateTypes.includes(
                    type.id,
                  )}
                />
                {type.name}
              </MenuItem>
            ))}
          </ClearableSelect>

          {selectedProject?.configuration?.samplesEnabled && (
            <ClearableSelect
              sx={{ mt: 2 }}
              fullWidth
              multiple={true}
              isClearable={true}
              onClear={() => {
                formik.setFieldValue('supportedSampleFormTemplateTypes', []);
              }}
              label={t('visits.supportedSampleFormTemplateTypes')}
              name="supportedSampleFormTemplateTypes"
              value={formik.values.supportedSampleFormTemplateTypes}
              onChange={e =>
                formik.setFieldValue(
                  'supportedSampleFormTemplateTypes',
                  e.target.value,
                )
              }
              renderValue={() => {
                return formik.values.supportedSampleFormTemplateTypes?.map(
                  sampleTemplateId => {
                    return (
                      <Chip
                        key={`add-visit-template-dialog:${sampleTemplateId}-value-chip`}
                        sx={{ mr: 1 }}
                        label={
                          sampleTemplateTypes?.find(
                            el => el.id === sampleTemplateId,
                          )?.name
                        }
                        color={'primary'}
                      />
                    );
                  },
                );
              }}
              error={
                formik.touched.supportedSampleFormTemplateTypes &&
                Boolean(formik.errors.supportedSampleFormTemplateTypes)
              }
              helperText={
                formik.touched.supportedSampleFormTemplateTypes &&
                formik.errors.supportedSampleFormTemplateTypes
              }
            >
              {sampleTemplateTypes?.map(type => (
                <MenuItem
                  key={`${type.id}-form-template-type-menu-item`}
                  value={type.id}
                  disabled={
                    type.formTemplates?.length === 1 &&
                    type.formTemplates[0].status === FormTemplateStatus.Draft
                  }
                >
                  <Checkbox
                    checked={formik.values.supportedSampleFormTemplateTypes.includes(
                      type.id,
                    )}
                  />
                  {type.name}
                </MenuItem>
              ))}
            </ClearableSelect>
          )}
          {isProjectAdmin && (
            <ClearableSelect
              sx={{ mt: 2 }}
              fullWidth
              multiple={true}
              isClearable={true}
              onClear={() => {
                formik.setFieldValue('supportedReportTemplateTypes', []);
              }}
              label={t('visits.supportedReportTemplateTypes')}
              name="supportedReportTemplateTypes"
              value={formik.values.supportedReportTemplateTypes}
              onChange={e =>
                formik.setFieldValue(
                  'supportedReportTemplateTypes',
                  e.target.value,
                )
              }
              renderValue={() => {
                return formik.values.supportedReportTemplateTypes?.map(
                  reportTemplateId => {
                    return (
                      <Chip
                        key={`add-visit-template-dialog:${reportTemplateId}-value-chip`}
                        sx={{ mr: 1 }}
                        label={
                          reportTemplateTypes?.find(
                            el => el.id === reportTemplateId,
                          )?.name
                        }
                        color={'primary'}
                      />
                    );
                  },
                );
              }}
              error={
                formik.touched.supportedReportTemplateTypes &&
                Boolean(formik.errors.supportedReportTemplateTypes)
              }
              helperText={
                formik.touched.supportedReportTemplateTypes &&
                formik.errors.supportedReportTemplateTypes
              }
            >
              {reportTemplateTypes?.map(type => (
                <MenuItem
                  key={`${type.id}-form-template-type-menu-item`}
                  value={type.id}
                  disabled={
                    type.formTemplates?.length === 1 &&
                    type.formTemplates[0].status === FormTemplateStatus.Draft
                  }
                >
                  <Checkbox
                    checked={formik.values.supportedReportTemplateTypes.includes(
                      type.id,
                    )}
                  />
                  {type.name}
                </MenuItem>
              ))}
            </ClearableSelect>
          )}
        </form>
      </DialogContent>
      <DialogActions>
        <Button
          onClick={() => {
            setSelectedVisitTemplate(null);
            closeDialog({ isOpen: false });
          }}
          color="secondary"
          variant="contained"
          sx={{ mb: 1 }}
        >
          {t('general.cancel')}
        </Button>
        <Button
          type="submit"
          color="primary"
          variant="contained"
          onClick={formik.submitForm}
          sx={{ mr: 2, mb: 1 }}
        >
          {selectedVisitTemplate?.id ? t('general.save') : t('general.create')}
        </Button>
      </DialogActions>
    </Box>
  );
}
